// Raw API Response Types (matching actual API)
export interface MerchantCouponCreateRawApiResponse {
    coupon_id: string;
    title: string;
    organization_statuses_count: number;
    organization_statuses_ids: string[] | null;
    message: string;
}

// Raw API Response for Coupon Detail (GET)
export interface MerchantCouponDetailRawApiResponse {
    id: string;
    title: string;
    description: string;
    code?: string;
    discount_type: 'percentage' | 'fixed';
    discount_value: string | number;
    quantity: number;
    remaining_quantity?: number;
    total_redeemed?: number;
    start_date: string;
    end_date: string;
    is_active: boolean;
    status?: string;
    limit_per_user: number;
    limit_per_day?: number;
    low_stock_threshold: number;
    alert_low_stock: boolean;
    is_low_stock?: boolean;
    points_required: number;
    minimum_purchase?: number;
    boost_coupon_visibility?: boolean;
    terms_and_conditions: string[];
    approval_status?: string;
    local_merchant_id?: string;
    creator_id?: string;
    created_at?: string;
    updated_at?: string;
    neighborhood_ids?: string[];
    time_slots?: Array<{
        day_of_week: number;
        start_time: string;
        end_time: string;
        is_day_active: boolean;
        is_hour_active: boolean;
    }>;
}

// API Create Request Types (matching actual API)
export interface MerchantCouponCreateApiRequest {
    title: string;
    description: string;
    discount_type: 'percentage' | 'fixed';
    discount_value: number;
    quantity: number;
    start_date: string; // ISO date string
    end_date: string; // ISO date string
    low_stock_threshold: number;
    alert_low_stock: boolean;
    limit_per_user: number;
    limit_per_day?: number;
    minimum_purchase?: number;
    boost_coupon_visibility?: boolean;
    terms_and_conditions: string[];
    points_required: number;
    neighborhood_ids?: string[];
    time_slots?: Array<{
        day_of_week: number; // 0=Monday, 6=Sunday
        start_time: string; // HH:mm format
        end_time: string; // HH:mm format
        is_day_active: boolean;
        is_hour_active: boolean;
    }>;
}

// API Update Request Types (matching actual API)
export interface MerchantCouponUpdateApiRequest {
    title: string;
    description: string;
    discount_type: 'percentage' | 'fixed';
    discount_value: number;
    quantity: number;
    start_date: string; // ISO date string
    end_date: string; // ISO date string
    low_stock_threshold: number;
    alert_low_stock: boolean;
    limit_per_user: number;
    limit_per_day?: number;
    minimum_purchase?: number;
    boost_coupon_visibility?: boolean;
    terms_and_conditions: string[];
    points_required: number;
    is_active?: boolean;
    neighborhood_ids?: string[];
    time_slots?: Array<{
        day_of_week: number; // 0=Monday, 6=Sunday
        start_time: string; // HH:mm format
        end_time: string; // HH:mm format
        is_day_active: boolean;
        is_hour_active: boolean;
    }>;
}

// Logic Model (API Request/Response)
export interface MerchantCouponEditorApiRequest {
    coupon_title: string;
    description: string;
    discount_type: 'percentage' | 'fixed_amount';
    discount_value: number;
    total_quantity_available: number;
    start_date: string; // ISO date string
    end_date: string; // ISO date string
    low_stock_threshold?: number;
    alert_low_stock: boolean;
    limit_per_user: number;
    limit_per_day?: number | null;
    terms_and_conditions: string[];
}

export interface MerchantCouponEditorApiResponse {
    id: string;
    coupon_title: string;
    description: string;
    discount_type: 'percentage' | 'fixed_amount';
    discount_value: number;
    total_quantity_available: number;
    start_date: string;
    end_date: string;
    low_stock_threshold?: number;
    alert_low_stock: boolean;
    limit_per_user: number;
    limit_per_day?: number | null;
    terms_and_conditions: string[];
    created_at: string;
    updated_at: string;
}

// UI Model (State Management)
export interface MerchantCouponUIState {
    id: string;
    coupon_title: string;
    description: string;
    image_url: string;
    image_file?: File | null; // Temporary field for file upload before creating coupon
    discount_type: 'percentage' | 'fixed_amount';
    discount_value: number;
    total_quantity_available: number;
    start_date: string | null;
    end_date: string | null;
    low_stock_threshold: number;
    alert_low_stock: boolean;
    limit_per_user: number;
    limit_per_day: number | null;
    terms_and_conditions: string[];
    points_required: number;
    isCreating: boolean;
    isLoading: boolean;
    // Plan-gated (Free / Basic / Premium)
    specific_days_enabled: boolean;
    specific_days: number[]; // 0=Mon..6=Sun
    weekdays_only: boolean; // Premium: "Weekdays only (exclude weekends)"
    specific_hours_enabled: boolean;
    start_time: string; // HH:mm
    end_time: string; // HH:mm
    require_min_purchase: boolean;
    min_purchase_amount: number;
    visibility_boost_48h: boolean;
}

// Helper to map API time_slots to UI state
const mapAPITimeSlotsToUI = (timeSlots?: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    is_day_active: boolean;
    is_hour_active: boolean;
}>): {
    specific_days_enabled: boolean;
    specific_days: number[];
    weekdays_only: boolean;
    specific_hours_enabled: boolean;
    start_time: string;
    end_time: string;
} => {
    if (!timeSlots || timeSlots.length === 0) {
        return {
            specific_days_enabled: false,
            specific_days: [],
            weekdays_only: false,
            specific_hours_enabled: false,
            start_time: '09:00',
            end_time: '17:00',
        };
    }

    // Extract days
    const days = timeSlots.map(slot => slot.day_of_week).sort((a, b) => a - b);
    
    // Check if weekdays only (all days are 0-4)
    const weekdaysOnly = days.length > 0 && days.every(day => day >= 0 && day <= 4) && days.length === 5;

    // Check if hours are active (all slots have is_hour_active = true)
    const hoursEnabled = timeSlots.length > 0 && timeSlots.every(slot => slot.is_hour_active);

    // Get start_time and end_time from first slot (assuming all slots have same hours)
    const firstSlot = timeSlots[0];
    const startTime = firstSlot?.start_time || '09:00';
    const endTime = firstSlot?.end_time || '17:00';

    return {
        specific_days_enabled: true,
        specific_days: days,
        weekdays_only: weekdaysOnly,
        specific_hours_enabled: hoursEnabled,
        start_time: startTime,
        end_time: endTime,
    };
};

// Mapper from Raw API Detail Response to UI State
export const mapRawDetailResponseToUI = (rawData: MerchantCouponDetailRawApiResponse): MerchantCouponUIState => {
    // Convert discount_type from 'fixed' to 'fixed_amount' to match UI
    const discountType: 'percentage' | 'fixed_amount' = rawData.discount_type === 'fixed' ? 'fixed_amount' : rawData.discount_type;

    // Convert discount_value from string to number
    const discountValue = typeof rawData.discount_value === 'string' 
        ? parseFloat(rawData.discount_value) || 0
        : rawData.discount_value || 0;

    // Map time slots
    const timeSlotMapping = mapAPITimeSlotsToUI(rawData.time_slots);

    return {
        id: rawData.id,
        coupon_title: rawData.title || '',
        description: rawData.description || '',
        image_url: '', // Not provided in API response
        image_file: null, // No file object from API response
        discount_type: discountType,
        discount_value: discountValue,
        total_quantity_available: rawData.quantity || 0,
        start_date: rawData.start_date || null,
        end_date: rawData.end_date || null,
        low_stock_threshold: rawData.low_stock_threshold || 10,
        alert_low_stock: rawData.alert_low_stock ?? true,
        limit_per_user: rawData.limit_per_user || 1,
        limit_per_day: rawData.limit_per_day ?? null,
        terms_and_conditions: rawData.terms_and_conditions && rawData.terms_and_conditions.length > 0
            ? rawData.terms_and_conditions
            : [''],
        points_required: rawData.points_required || 0,
        isCreating: false,
        isLoading: false,
        specific_days_enabled: timeSlotMapping.specific_days_enabled,
        specific_days: timeSlotMapping.specific_days,
        weekdays_only: timeSlotMapping.weekdays_only,
        specific_hours_enabled: timeSlotMapping.specific_hours_enabled,
        start_time: timeSlotMapping.start_time,
        end_time: timeSlotMapping.end_time,
        require_min_purchase: rawData.minimum_purchase !== undefined && rawData.minimum_purchase > 0,
        min_purchase_amount: rawData.minimum_purchase || 0,
        visibility_boost_48h: rawData.boost_coupon_visibility || false,
    };
};

// Data Mapper
export const mapApiResponseToUI = (apiData: MerchantCouponEditorApiResponse): MerchantCouponUIState => {
    return {
        id: apiData.id,
        coupon_title: apiData.coupon_title || '',
        description: apiData.description || '',
        image_url: '', // Not provided in API response
        image_file: null, // No file object from API response
        discount_type: apiData.discount_type || 'percentage',
        discount_value: apiData.discount_value || 0,
        total_quantity_available: apiData.total_quantity_available || 0,
        start_date: apiData.start_date || null,
        end_date: apiData.end_date || null,
        low_stock_threshold: apiData.low_stock_threshold || 10,
        alert_low_stock: apiData.alert_low_stock ?? true,
        limit_per_user: apiData.limit_per_user || 1,
        limit_per_day: apiData.limit_per_day ?? null,
        terms_and_conditions: apiData.terms_and_conditions || [],
        points_required: 0, // Not provided in API response
        isCreating: false,
        isLoading: false,
        specific_days_enabled: false,
        specific_days: [],
        weekdays_only: false,
        specific_hours_enabled: false,
        start_time: '09:00',
        end_time: '17:00',
        require_min_purchase: false,
        min_purchase_amount: 0,
        visibility_boost_48h: false,
    };
};

export const mapUIToApiRequest = (uiData: MerchantCouponUIState): MerchantCouponEditorApiRequest => {
    return {
        coupon_title: uiData.coupon_title,
        description: uiData.description,
        discount_type: uiData.discount_type,
        discount_value: uiData.discount_value,
        total_quantity_available: uiData.total_quantity_available,
        start_date: uiData.start_date || '',
        end_date: uiData.end_date || '',
        low_stock_threshold: uiData.low_stock_threshold,
        alert_low_stock: uiData.alert_low_stock,
        limit_per_user: uiData.limit_per_user,
        limit_per_day: uiData.limit_per_day,
        terms_and_conditions: uiData.terms_and_conditions.filter(term => term.trim().length > 0),
    };
};

// Helper to map UI time slots to API time_slots format
const mapUITimeSlotsToAPI = (uiData: MerchantCouponUIState): Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    is_day_active: boolean;
    is_hour_active: boolean;
}> => {
    // If specific_days_enabled is false, return empty array (no time restrictions)
    if (!uiData.specific_days_enabled) {
        return [];
    }

    // Get days to include (if weekdays_only, use weekdays; otherwise use specific_days)
    const daysToInclude = uiData.weekdays_only 
        ? [0, 1, 2, 3, 4] // Monday to Friday
        : (uiData.specific_days || []);

    // Map each day to time_slot
    return daysToInclude.map(day => ({
        day_of_week: day,
        start_time: uiData.specific_hours_enabled ? (uiData.start_time || '09:00') : '00:00',
        end_time: uiData.specific_hours_enabled ? (uiData.end_time || '23:59') : '23:59',
        is_day_active: true, // Day is active if it's in specific_days
        is_hour_active: uiData.specific_hours_enabled, // Hour restriction is active if specific_hours_enabled
    }));
};

// Mapper from UI State to API Create Request
export const mapUIToCreateRequest = (uiData: MerchantCouponUIState): MerchantCouponCreateApiRequest => {
    // Map discount_type from 'fixed_amount' to 'fixed' (API uses 'fixed')
    const discountType: 'percentage' | 'fixed' = uiData.discount_type === 'fixed_amount' ? 'fixed' : uiData.discount_type;

    // Map time slots
    const timeSlots = mapUITimeSlotsToAPI(uiData);

    return {
        title: uiData.coupon_title,
        description: uiData.description,
        discount_type: discountType,
        discount_value: uiData.discount_value,
        quantity: uiData.total_quantity_available,
        start_date: uiData.start_date || '',
        end_date: uiData.end_date || '',
        low_stock_threshold: uiData.low_stock_threshold || 10,
        alert_low_stock: uiData.alert_low_stock,
        limit_per_user: uiData.limit_per_user,
        limit_per_day: uiData.limit_per_day ?? undefined,
        minimum_purchase: uiData.require_min_purchase ? uiData.min_purchase_amount : undefined,
        boost_coupon_visibility: uiData.visibility_boost_48h || false,
        terms_and_conditions: uiData.terms_and_conditions.filter(term => term.trim().length > 0),
        points_required: uiData.points_required || 0,
        neighborhood_ids: [], // TODO: Add neighborhood_ids field to UI state if needed
        time_slots: timeSlots.length > 0 ? timeSlots : undefined,
    };
};

// Mapper from UI State to API Update Request
export const mapUIToUpdateRequest = (uiData: MerchantCouponUIState): MerchantCouponUpdateApiRequest => {
    // Map discount_type from 'fixed_amount' to 'fixed' (API uses 'fixed')
    const discountType: 'percentage' | 'fixed' = uiData.discount_type === 'fixed_amount' ? 'fixed' : uiData.discount_type;

    // Map time slots
    const timeSlots = mapUITimeSlotsToAPI(uiData);

    return {
        title: uiData.coupon_title,
        description: uiData.description,
        discount_type: discountType,
        discount_value: uiData.discount_value,
        quantity: uiData.total_quantity_available,
        start_date: uiData.start_date || '',
        end_date: uiData.end_date || '',
        low_stock_threshold: uiData.low_stock_threshold || 10,
        alert_low_stock: uiData.alert_low_stock,
        limit_per_user: uiData.limit_per_user,
        limit_per_day: uiData.limit_per_day ?? undefined,
        minimum_purchase: uiData.require_min_purchase ? uiData.min_purchase_amount : undefined,
        boost_coupon_visibility: uiData.visibility_boost_48h || false,
        terms_and_conditions: uiData.terms_and_conditions.filter(term => term.trim().length > 0),
        points_required: uiData.points_required || 0,
        is_active: true, // Default to true, can be updated later if needed
        neighborhood_ids: [], // TODO: Add neighborhood_ids field to UI state if needed
        time_slots: timeSlots.length > 0 ? timeSlots : undefined,
    };
};


// Initial UI State
export const createInitialCouponState = (): MerchantCouponUIState => {
    return {
        id: '',
        coupon_title: '',
        description: '',
        image_url: '',
        image_file: null,
        discount_type: 'percentage',
        discount_value: 0,
        total_quantity_available: 0,
        start_date: null,
        end_date: null,
        low_stock_threshold: 10,
        alert_low_stock: true,
        limit_per_user: 1,
        limit_per_day: null,
        terms_and_conditions: [''],
        points_required: 0,
        isCreating: false,
        isLoading: false,
        specific_days_enabled: false,
        specific_days: [],
        weekdays_only: false,
        specific_hours_enabled: false,
        start_time: '09:00',
        end_time: '17:00',
        require_min_purchase: false,
        min_purchase_amount: 0,
        visibility_boost_48h: false,
    };
};
